/**
 * `DocumentFloorPlanVertical` / `DocumentFloorPlanHorizontal` 공통 — 캡션 수집·청크·PDF 캡션 뷰.
 */

import React from 'react';
import type { Style } from '@react-pdf/types';
import { View } from '@react-pdf/renderer';
import {
  PdfLayout,
  ProjectElementType,
  WorkType,
  usesWorkDuration,
} from '../../shared/enum/EnumRepository';
import { convertWorkSizeToString, PEWorkData, ProjectElementData } from '../../shared/dto/EntityRepository';
import { CvPdfText } from '../about/cvPdfShared';
import { getFloorPlanCaptionSlotPlan } from './floorPlanCaptionLimits';
import type { FloorPlanCaptionLayout } from './floorPlanA4PreviewTypes';
import { rasterBlobToJpegBlob } from '../../shared/util/workListExportThumbRaster';
import { resolveWorkArtistName } from '../../shared/util/workArtist';

export type CaptionBlock = {
  peId: string;
  index: number;
  workType: WorkType;
  title: string;
  artist: string;
  prodYear: string;
  material: string;
  sizeLine: string;
  duration: string;
};

/** @react-pdf `View`/`Text` 스타일 — 문서별 StyleSheet와 호환 */
export type FloorPlanPdfCaptionStyleRefs = {
  captionColumnsWrap: Style;
  captionRowsWrap: Style;
  captionColumn: Style;
  /** Row — 번호와 본문이 한 줄(가로)·작품별로 줄 바꿈 */
  captionRowSingleLine: Style;
  /** Row 본문 `Text` 바깥 `View`(flex 허용) — `CvPdfText` 자체에는 flex 피함 */
  captionRowTextWrap: Style;
  captionBlock: Style;
  captionRight: Style;
  captionIndex: Style;
  captionTitle: Style;
  captionLine: Style;
  captionRowInline: Style;
};

export const asTrimmedString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
};

export const dashIfEmpty = (value: string): string => (value ? value : '-');

export const getWorkSizeLine = (work: PEWorkData): string => {
  if (usesWorkDuration(work.workType)) return '';
  return convertWorkSizeToString(work.size, work.dimensionsType, work.sizeText);
};

export const getWorkCaptionFields = (
  peId: string,
  work: PEWorkData,
  includeArtist = true,
): Omit<CaptionBlock, 'index'> => ({
  peId,
  workType: work.workType,
  title: asTrimmedString(work.title),
  artist: includeArtist ? resolveWorkArtistName(work.artist) : '',
  prodYear: asTrimmedString(work.prodYear),
  material: asTrimmedString(work.material),
  sizeLine: getWorkSizeLine(work),
  duration: asTrimmedString(work.duration),
});

export function collectFloorPlanCaptionBlocks(
  projectElements: ProjectElementData[],
  maxSlots: number,
  includeArtist = true,
): CaptionBlock[] {
  const blocks: CaptionBlock[] = [];
  let ordinal = 0;
  for (const pe of projectElements) {
    if (ordinal >= maxSlots) break;
    if (pe.projectElementType !== ProjectElementType.WORK || !pe.work) continue;
    const fields = getWorkCaptionFields(pe.id, pe.work, includeArtist);
    ordinal += 1;
    blocks.push({ ...fields, index: ordinal });
  }
  return blocks;
}

export function chunkCaptionSegments(blocks: CaptionBlock[], chunkSize: number): CaptionBlock[][] {
  const segments: CaptionBlock[][] = [];
  for (let i = 0; i < blocks.length; i += chunkSize) {
    segments.push(blocks.slice(i, i + chunkSize));
  }
  return segments;
}

export function buildCaptionSegments(
  projectElements: ProjectElementData[],
  layout: FloorPlanCaptionLayout,
  pdfSheetLayout: PdfLayout,
  includeArtist = true,
): CaptionBlock[][] {
  const { maxSlots, segmentChunkSize } = getFloorPlanCaptionSlotPlan(pdfSheetLayout, layout);
  const blocks = collectFloorPlanCaptionBlocks(projectElements, maxSlots, includeArtist);
  return chunkCaptionSegments(blocks, segmentChunkSize);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('readAsDataURL failed'));
    reader.readAsDataURL(blob);
  });
}

/** 도면 장변 상한(px) — WebP 등은 JPEG로 두는 편이 @react-pdf `<Image>`와 맞춤(`workListExportThumbRaster`). */
const FLOOR_PLAN_PDF_IMAGE_MAX_PX = 2400;

/**
 * 사용자 도면 blob / data → PDF 합성용 `data:image/jpeg` 권장.
 * - blob: fetch 불안정 + WebP 등은 react-pdf가 비는 경우 많음 → canvas JPEG data URL 로 통일
 * - 그 외(번들 정적 문자열 등) 그대로 반환
 */
export async function resolveFloorPlanImageSrcForPdf(src: string): Promise<string> {
  if (!src.startsWith('blob:') && !src.startsWith('data:')) return src;

  const res = await fetch(src);
  if (!res.ok) throw new Error(`floor plan image fetch failed: ${res.status}`);
  const blob = await res.blob();
  const jpegBlob = await rasterBlobToJpegBlob(blob, FLOOR_PLAN_PDF_IMAGE_MAX_PX);
  return blobToDataUrl(jpegBlob ?? blob);
}

export function floorPlanImageSource(src: string): string | { uri: string } {
  if (src.startsWith('blob:') || src.startsWith('http') || src.startsWith('data:')) {
    return { uri: src };
  }
  if (typeof window !== 'undefined' && src.startsWith('/')) {
    return { uri: `${window.location.origin}${src}` };
  }
  return src;
}

/** Row — 작가 · 제목 · 연도 · 재료 · 크기(또는 duration) 를 한 줄 문자열로 */
export function formatFloorPlanCaptionOneLine(
  block: CaptionBlock,
  includeArtist = true,
): string {
  const last = dashIfEmpty(usesWorkDuration(block.workType) ? block.duration : block.sizeLine);
  return [
    ...(includeArtist ? [dashIfEmpty(block.artist)] : []),
    dashIfEmpty(block.title),
    dashIfEmpty(block.prodYear),
    dashIfEmpty(block.material),
    last,
  ].join('  ·  ');
}

const renderCaptionBlockColumn = (
  block: CaptionBlock,
  s: FloorPlanPdfCaptionStyleRefs,
  layoutKeyPrefix: string,
  includeArtist: boolean,
) => (
  <View key={`${layoutKeyPrefix}-${block.peId}`} style={s.captionBlock}>
    <CvPdfText style={s.captionIndex}>{`${block.index}.`}</CvPdfText>
    <View style={s.captionRight}>
      {includeArtist && (
        <CvPdfText style={s.captionLine}>{dashIfEmpty(block.artist)}</CvPdfText>
      )}
      <CvPdfText style={s.captionTitle}>{dashIfEmpty(block.title)}</CvPdfText>
      <CvPdfText style={s.captionLine}>{dashIfEmpty(block.prodYear)}</CvPdfText>
      <CvPdfText style={s.captionLine}>{dashIfEmpty(block.material)}</CvPdfText>
      <CvPdfText style={s.captionLine}>
        {dashIfEmpty(usesWorkDuration(block.workType) ? block.duration : block.sizeLine)}
      </CvPdfText>
    </View>
  </View>
);

export const FloorPlanPdfCaptions: React.FC<{
  captionSegments: CaptionBlock[][];
  captionLayout: FloorPlanCaptionLayout;
  includeArtist: boolean;
  s: FloorPlanPdfCaptionStyleRefs;
}> = ({ captionSegments, captionLayout, includeArtist, s }) => {
  if (captionSegments.length === 0) return null;

  /** Column — 래퍼 가로열 + 각 세그먼트 = 단(세로 블록 쌓기) · Row는 별 JSX로 분리 */
  if (captionLayout === 'column') {
    return (
      <View style={s.captionColumnsWrap}>
        {captionSegments.map((segment, segIndex) => (
          <View key={`fp-col-${segIndex}`} style={s.captionColumn}>
            {segment.map((block) =>
              renderCaptionBlockColumn(block, s, `col-${segIndex}`, includeArtist)
            )}
          </View>
        ))}
      </View>
    );
  }

  /** Row — 작품 순서대로 한 줄 한 작품(`segmentChunkSize: 1`과 정합) */
  return (
    <View style={s.captionRowsWrap}>
      {captionSegments.flatMap((segment, segIndex) =>
        segment.map((block) => (
          <View key={`fp-row-${segIndex}-${block.peId}`} style={s.captionRowSingleLine}>
            <CvPdfText style={s.captionIndex}>{`${block.index}.`}</CvPdfText>
            <View style={s.captionRowTextWrap}>
              <CvPdfText style={s.captionRowInline}>
                {formatFloorPlanCaptionOneLine(block, includeArtist)}
              </CvPdfText>
            </View>
          </View>
        ))
      )}
    </View>
  );
};
